import { useState, useMemo } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import Grid from "@mui/material/Grid2";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Card,
  CardContent,
  useTheme,
  Tooltip,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import { isValidColor } from '../../../globalStore/utils/helpers';
import { useSelector } from 'react-redux';

const CHART_TYPES = {
  BAR_STACKED: 'bar_stacked',
  BAR_GROUPED: 'bar_grouped',
  LINE: 'line',
  AREA: 'area',
  PIE: 'pie',
  DONUT: 'donut',
};

function DailyEventsBar(props) {
  const {
    barDatasets,
    barDatasets2,
    eventsGridRef,
    recentEvents,
    setSelectedBar,
  } = props;

  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");

  const [chartType, setChartType] = useState(CHART_TYPES.BAR_STACKED);
  const { data = [], series = [] } = barDatasets2;
  const allEventTypesMap = useSelector((state) => state.organization.organization.allEventTypesMap);

  const colorPalette = [
    '#3366E6', '#F472B6', '#FB923C', '#00B3E6', '#A3E635', '#A855F7', '#999966', '#FF6633', '#22C55E', '#EF4444',
    '#80B300', '#FACC15', '#E6B3B3', '#6680B3', '#66991A', '#FF99E6', '#FF1A66', '#E6331A', '#33FFCC', '#FFB399',
    '#FF33FF', '#FFFF99', '#0EA5E9', '#E6B333', '#8B5CF6', '#14B8A6', '#99FF99', '#F43F5E', '#84CC16', '#06B6D4',
    '#E6B3B3', '#6680B3', '#66991A', '#FF99E6', '#FF1A66', '#E6331A', '#33FFCC', '#FFB399', '#FF33FF', '#FFFF99',
    '#CCFF1A', '#FF1A66', '#E6331A', '#33FFCC', '#FFB399'
  ];

  const handleChartTypeChange = (event, newChartType) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  const handleLabelClick = (data) => {
    const { axisValue } = data || {};
    setSelectedBar({ date: axisValue });
    eventsGridRef.current?.focus();
  };

  const dataSets = useMemo(() => {
    const sets = [];
    for (let key in barDatasets) {
      sets.push({ label: key, data: barDatasets[key] });
    }
    colorPalette.forEach((color, index) => {
      const currDataSet = sets[index];
      const currDataSetLabel = currDataSet?.label;
      if (currDataSet && allEventTypesMap?.[currDataSetLabel] && isValidColor(allEventTypesMap[currDataSetLabel]?.color)) {
        currDataSet.backgroundColor = allEventTypesMap[currDataSetLabel].color;
      } else if (currDataSet) {
        currDataSet.backgroundColor = color;
      }
    });
    return sets;
  }, [barDatasets, allEventTypesMap]);

  const coloredSeries = useMemo(() => {
    return series.map((s, index) => {
      const eventColor = allEventTypesMap?.[s.dataKey] && isValidColor(allEventTypesMap[s.dataKey]?.color)
        ? allEventTypesMap[s.dataKey].color
        : colorPalette[index % colorPalette.length];
      return {
        ...s,
        color: eventColor,
        ...(chartType === CHART_TYPES.AREA && { area: true }),
        ...(chartType === CHART_TYPES.BAR_GROUPED && { stack: undefined }),
      };
    });
  }, [series, allEventTypesMap, chartType]);

  const pieData = useMemo(() => {
    const eventCounts = {};
    recentEvents?.forEach(event => {
      const eventType = event.event;
      eventCounts[eventType] = (eventCounts[eventType] || 0) + 1;
    });
    return Object.entries(eventCounts).map(([label, value], index) => {
      const eventColor = allEventTypesMap?.[label] && isValidColor(allEventTypesMap[label]?.color)
        ? allEventTypesMap[label].color
        : colorPalette[index % colorPalette.length];
      return { id: index, value, label, color: eventColor };
    });
  }, [recentEvents, allEventTypesMap]);

  const renderChart = () => {
    const commonHeight = 280;

    switch (chartType) {
      case CHART_TYPES.BAR_STACKED:
      case CHART_TYPES.BAR_GROUPED:
        return (
          <BarChart
            colors={colorPalette}
            dataset={data}
            height={commonHeight}
            onAxisClick={(event, data) => handleLabelClick(data)}
            series={coloredSeries}
            slotProps={{
              legend: {
                hidden: false,
                direction: 'row',
                position: { vertical: 'top', horizontal: 'middle' },
                labelStyle: { fontSize: 11, fill: textSecondary },
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                markGap: 5,
                itemGap: 15,
              }
            }}
            xAxis={[{ dataKey: 'date', scaleType: 'band', tickLabelStyle: { fontSize: 10, fill: textSecondary } }]}
            yAxis={[{ tickLabelStyle: { fontSize: 10, fill: textSecondary } }]}
            sx={{
              '& .MuiChartsAxis-line': { stroke: borderColor },
              '& .MuiChartsAxis-tick': { stroke: borderColor },
            }}
          />
        );

      case CHART_TYPES.LINE:
      case CHART_TYPES.AREA:
        return (
          <LineChart
            colors={colorPalette}
            dataset={data}
            height={commonHeight}
            series={coloredSeries}
            slotProps={{
              legend: {
                hidden: false,
                direction: 'row',
                position: { vertical: 'top', horizontal: 'middle' },
                labelStyle: { fontSize: 11, fill: textSecondary },
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                markGap: 5,
                itemGap: 15,
              }
            }}
            xAxis={[{ dataKey: 'date', scaleType: 'band', tickLabelStyle: { fontSize: 10, fill: textSecondary } }]}
            yAxis={[{ tickLabelStyle: { fontSize: 10, fill: textSecondary } }]}
            sx={{
              '& .MuiChartsAxis-line': { stroke: borderColor },
              '& .MuiChartsAxis-tick': { stroke: borderColor },
            }}
          />
        );

      case CHART_TYPES.PIE:
      case CHART_TYPES.DONUT:
        return (
          <PieChart
            height={commonHeight}
            margin={{ top: 60, bottom: 60, left: 100, right: 100 }}
            series={[
              {
                data: pieData,
                innerRadius: chartType === CHART_TYPES.DONUT ? 50 : 0,
                outerRadius: 90,
                paddingAngle: 2,
                cornerRadius: 4,
                highlightScope: { fade: 'global', highlight: 'item' },
                faded: { innerRadius: 30, additionalRadius: -10, color: 'gray' },
              }
            ]}
            slotProps={{
              legend: {
                direction: 'row',
                position: { vertical: 'bottom', horizontal: 'middle' },
                labelStyle: { fontSize: 11, fill: textSecondary },
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                markGap: 5,
                itemGap: 10,
              },
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Grid size={12}>
      <Card
        sx={{
          borderRadius: "16px",
          border: `1px solid ${borderColor}`,
          background: isDark
            ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
            : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 4px 20px rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: isDark
              ? `linear-gradient(90deg, ${greenColor} 0%, ${greenColor}60 100%)`
              : `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
          },
        }}
      >
        <CardContent sx={{ padding: "16px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
            <Typography variant="h6" sx={{ color: isDark ? greenColor : navyColor, fontWeight: 700, fontSize: "1rem" }}>
              Daily Events Overview
            </Typography>

            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={handleChartTypeChange}
              aria-label="Chart type selection"
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: `1px solid ${borderColor}`,
                  color: textSecondary,
                  padding: '6px 12px',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(160, 196, 109, 0.1)' : 'rgba(0, 40, 67, 0.05)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: isDark ? 'rgba(160, 196, 109, 0.2)' : 'rgba(0, 40, 67, 0.1)',
                    color: isDark ? greenColor : navyColor,
                    borderColor: isDark ? greenColor : navyColor,
                  },
                },
              }}
            >
              <Tooltip title="Stacked Bar" arrow><ToggleButton value={CHART_TYPES.BAR_STACKED}><BarChartIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Grouped Bar" arrow><ToggleButton value={CHART_TYPES.BAR_GROUPED}><BarChartIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} /></ToggleButton></Tooltip>
              <Tooltip title="Line Chart" arrow><ToggleButton value={CHART_TYPES.LINE}><ShowChartIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Area Chart" arrow><ToggleButton value={CHART_TYPES.AREA}><StackedLineChartIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Pie Chart" arrow><ToggleButton value={CHART_TYPES.PIE}><PieChartIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Donut Chart" arrow><ToggleButton value={CHART_TYPES.DONUT}><DonutLargeIcon fontSize="small" /></ToggleButton></Tooltip>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ width: '100%', minHeight: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {renderChart()}
          </Box>

          <Box sx={{ display: 'flex', gap: '16px', marginTop: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Box sx={{ padding: '8px 16px', borderRadius: '8px', background: isDark ? 'rgba(160, 196, 109, 0.1)' : 'rgba(0, 40, 67, 0.05)', border: `1px solid ${isDark ? 'rgba(160, 196, 109, 0.2)' : 'rgba(0, 40, 67, 0.1)'}` }}>
              <Typography sx={{ fontSize: '0.75rem', color: textSecondary }}>
                Total Events: <strong style={{ color: isDark ? greenColor : navyColor }}>{recentEvents?.length || 0}</strong>
              </Typography>
            </Box>
            <Box sx={{ padding: '8px 16px', borderRadius: '8px', background: isDark ? 'rgba(160, 196, 109, 0.1)' : 'rgba(0, 40, 67, 0.05)', border: `1px solid ${isDark ? 'rgba(160, 196, 109, 0.2)' : 'rgba(0, 40, 67, 0.1)'}` }}>
              <Typography sx={{ fontSize: '0.75rem', color: textSecondary }}>
                Event Types: <strong style={{ color: isDark ? greenColor : navyColor }}>{pieData?.length || 0}</strong>
              </Typography>
            </Box>
            <Box sx={{ padding: '8px 16px', borderRadius: '8px', background: isDark ? 'rgba(160, 196, 109, 0.1)' : 'rgba(0, 40, 67, 0.05)', border: `1px solid ${isDark ? 'rgba(160, 196, 109, 0.2)' : 'rgba(0, 40, 67, 0.1)'}` }}>
              <Typography sx={{ fontSize: '0.75rem', color: textSecondary }}>
                Time Range: <strong style={{ color: isDark ? greenColor : navyColor }}>Last 28 Days</strong>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

export default DailyEventsBar;
